import type { MedicineMovement } from '@/types';

/** The translation and number formatting a details line needs, injected so the wording is testable without the i18n plugin. */
export type MovementDetailsFormat = {
    t: (key: string) => string;
    n: (value: number) => string;
};

const KEY_PREFIX = 'views.health.inventory.details.';

/**
 * The one-line description of a movement shown in the Details column: what was bought, which
 * treatment consumed it, or what a count found. Parts are joined with a middle dot; an entry with
 * nothing to say reads blank.
 */
export function movementDetailsText(movement: MedicineMovement, { t, n }: MovementDetailsFormat): string {
    const { details } = movement;
    const parts: string[] = [];

    switch (movement.type) {
        case 'purchase':
            if (details.packs !== undefined && details.pack_size !== undefined) {
                parts.push(t(`${KEY_PREFIX}purchasePacks`).replace('%PACKS%', n(details.packs)).replace('%SIZE%', n(details.pack_size)).replace('%UNIT%', movement.unit_name));
            }
            if (details.batch_number) {
                parts.push(t(`${KEY_PREFIX}batch`).replace('%BATCH%', details.batch_number));
            }
            break;
        case 'treatment':
        case 'retreatment':
            if (details.location_text) {
                parts.push(t(`${KEY_PREFIX}location`).replace('%LOCATION%', details.location_text));
            }
            if (details.animals !== undefined) {
                parts.push(t(`${KEY_PREFIX}animals`).replace('%ANIMALS%', n(details.animals)));
            }
            if (details.treatment_day !== null && details.treatment_day !== undefined) {
                parts.push(
                    details.repetitions !== null && details.repetitions !== undefined
                        ? t(`${KEY_PREFIX}treatmentDayOf`).replace('%DAY%', String(details.treatment_day)).replace('%TOTAL%', String(details.repetitions))
                        : t(`${KEY_PREFIX}treatmentDay`).replace('%DAY%', String(details.treatment_day))
                );
            }
            break;
        case 'count':
            if (details.counted !== undefined && details.calculated !== undefined) {
                parts.push(t(`${KEY_PREFIX}counted`).replace('%COUNTED%', n(details.counted)).replace('%CALCULATED%', n(details.calculated)));
            }
            if (details.reason_name) {
                parts.push(t(`${KEY_PREFIX}reason`).replace('%REASON%', details.reason_name));
            }
            break;
        default:
            break;
    }

    if (details.note) {
        parts.push(details.note);
    }

    return parts.join(' · ');
}
